import {
	FormControl,
	InputBaseProps,
	MenuItem,
	Select as MUISelect,
	SxProps,
	IconButton,
} from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { InputLabel } from 'shared/ui/InputLabel';
import { SelectOption } from 'shared/ui/Select/types';
import { InputError } from 'shared/ui/InputError';

export interface SelectProps extends Omit<InputBaseProps, 'onSelect'> {
	label?: string;
	value: string | string[] | undefined;
	defaultValue?: string | string[] | undefined;
	placeholder: string;
	onSelect?: (value: string | string[] | undefined) => void;
	onClear?: () => void;
	options: Array<SelectOption>;
	multiple?: boolean;
	errorText?: string;
	disabled?: boolean;
	sxError?: SxProps;
}

export const Select: FC<SelectProps> = ({
	sx,
	sxError,
	label,
	value,
	placeholder,
	onSelect,
	onClear,
	options,
	multiple,
	errorText,
	required,
	disabled,
	defaultValue,
	size,
}) => {
	const { t } = useTranslation();
	const showClear = !!onClear && !!value && !Array.isArray(value);

	return (
		<FormControl
			variant={'standard'}
			fullWidth={true}
			sx={{ marginBottom: '15px', ...sx }}
			error={required}
		>
			{label && <InputLabel name={label} label={label} required={required} />}
			<MUISelect
				defaultValue={defaultValue}
				disabled={disabled}
				multiple={multiple}
				value={value}
				IconComponent={() => (
					<Icon
						id={'custom_arrow_down'}
						style={{ width: '18px', height: '9px', marginRight: '10px' }}
						color={ColorPalette.astronaut}
					/>
				)}
				displayEmpty={true}
				renderValue={(value) => {
					if (!value || !value?.length) {
						return placeholder;
					} else {
						if (Array.isArray(value)) {
							const valueLabels = options
								.filter((option) => {
									return option.value && value.includes(option.value);
								})
								.map((option) => t(option.label) as string);
							return valueLabels.join(', ');
						} else {
							const returnValue = options.find((option) => {
								return option.value === value;
							})?.label;
							return t(returnValue as string);
						}
					}
				}}
				onChange={(event) => onSelect && onSelect(event.target.value)}
				MenuProps={{
					PaperProps: {
						ref: (el: HTMLElement | null) => {
							if (el) el.style.maxWidth = el.style.minWidth;
						},
					},
				}}
				sx={{
					'position': 'relative',
					'margin': '0',
					'border': `1px solid ${errorText ? ColorPalette.violetRed : ColorPalette.periwinkle}`,
					'borderRadius': '10px',

					'color': value ? ColorPalette.astronaut : ColorPalette.baliHai,
					'fontWeight': 600,
					'fontSize': '14px',

					'&:placeholder': {
						color: ColorPalette.baliHai,
					},
					'& .MuiInputBase-input': {
						display: 'flex',
						alignItems: 'center',
						height: '100%',
						padding: `${size === 'small' ? '9px' : '20px 16px'}`,
					},
					'& .MuiInputBase-input:focus': {
						backgroundColor: 'transparent',
					},
					'&:before, &:after': {
						display: 'none',
					},
					'label + &': {
						marginTop: '18px',
					},
				}}
			>
				{options.map((option) => (
					<MenuItem key={String(option.value)} value={option.value ?? ''}>
						{option.customLabel ? option.customLabel : t(option.label)}
					</MenuItem>
				))}
			</MUISelect>
			{showClear && (
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onClear!();
					}}
					sx={{
						position: 'absolute',
						right: '38px',
						top: label ? 'calc(50% + 9px)' : '50%',
						transform: 'translateY(-50%)',
						zIndex: 1,
					}}
				>
					<Icon
						id={'close_round'}
						style={{ width: '14px', height: '14px' }}
						color={ColorPalette.baliHai}
					/>
				</IconButton>
			)}
			{errorText && <InputError text={t(errorText) as string} sx={{ ...sxError }} />}
		</FormControl>
	);
};
